import type { Media } from "@shared";

/**
 * The wall's playlist, as pure functions.
 *
 * Kept out of the component because the rules have exactly one correct answer
 * and none of them need a DOM: pinned first, newest first within each group,
 * a new arrival joins without becoming the slide, and stepping wraps. A browser
 * test is the right place for timing; this is the right place for ordering.
 */

const newestFirst = (a: Media, b: Media): number => b.createdAt.localeCompare(a.createdAt);

/**
 * The order a slideshow plays in.
 *
 * Pinned media is the host's explicit choice, so it is seen before the flood.
 * Within each group the newest photo is the most interesting one to the room,
 * which is why the grid already orders that way.
 */
export function orderPlaylist(items: readonly Media[]): Media[] {
  return [...items].sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    return newestFirst(a, b);
  });
}

/**
 * Fold a batch of arrivals into a list without duplicating ids.
 *
 * `arrivals` wins on a collision so a moderation change or a fresh derived URL
 * takes effect. The result is ordered, because every caller wants to render or
 * rotate through it immediately.
 */
export function mergeArrivals(
  items: readonly Media[],
  arrivals: readonly Media[],
): Media[] {
  const merged = new Map<string, Media>();
  for (const item of items) merged.set(item.mediaId, item);
  for (const item of arrivals) merged.set(item.mediaId, item);
  return orderPlaylist([...merged.values()]);
}

/**
 * The item `direction` steps from `currentId`, wrapping at both ends.
 *
 * A missing current id — first paint, or the slide was deleted — starts at the
 * front for a forward step and at the back for a backward one, which is what a
 * previous button on an empty cursor should do.
 */
export function nextItem(
  items: readonly Media[],
  currentId: string | null,
  direction: 1 | -1,
): Media | null {
  if (items.length === 0) return null;

  const index = currentId ? items.findIndex((item) => item.mediaId === currentId) : -1;
  if (index === -1) return direction === 1 ? items[0]! : items[items.length - 1]!;

  const next = (index + direction + items.length) % items.length;
  return items[next]!;
}

/** Advance one slide. Named separately because that is how the timer reads. */
export function rotate(items: readonly Media[], currentId: string | null): Media | null {
  return nextItem(items, currentId, 1);
}
